import React from 'react'

import isEmpty from 'lodash/isEmpty'
import noop from 'lodash/noop'

import cn from '../../libs/class-names'

import {CommonProps, Overwrite} from '../../types'

import SelectMenuItem from './SelectMenuItem'

const KEY_SEARCH_TIMEOUT = 400

interface Value {
  value: unknown
  label?: React.ReactNode // When not provided value will be shown
  // Feel free to attach more data here and use it in the children render prop for item rendering.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any
}

type Props = Overwrite<
  CommonProps<'ul'>,
  {
    values?: Value[]
    children?: (item: Value, selectedValue: boolean) => React.ReactElement
    // Automatically set by SelectField
    selectedValue?: Value | Value[]
    onClick?: (value: Value) => void
  }
>

const getLabel = (label?: React.ReactNode, value?: unknown) => label || `${value}`

const defaultChildren = ({value, label}: Value, selected: boolean) => (
  <SelectMenuItem selected={selected}>{getLabel(label, value)}</SelectMenuItem>
)

class SelectMenu extends React.Component<Props> {
  itemRefs: {[key: string]: HTMLLIElement} = {}

  menuRef = React.createRef<HTMLUListElement>()

  lastKey = ''

  lastKeyTimeout?: NodeJS.Timeout = undefined

  componentWillUnmount() {
    if (this.lastKeyTimeout) {
      clearTimeout(this.lastKeyTimeout)
    }
  }

  // eslint-disable-next-line react/no-unused-class-component-methods
  handleKeyDown = (keyCode: string) => {
    this.lastKey = `${this.lastKey}${keyCode}`

    if (this.lastKeyTimeout) {
      clearTimeout(this.lastKeyTimeout)
    }

    this.lastKeyTimeout = setTimeout(() => {
      this.lastKey = ''
    }, KEY_SEARCH_TIMEOUT)

    const firstFoundItemKey = Object.keys(this.itemRefs).find(label => {
      return label.toLocaleLowerCase().startsWith(this.lastKey)
    })

    if (firstFoundItemKey && this.itemRefs[firstFoundItemKey]) {
      this.menuRef.current?.scroll({
        top: this.itemRefs[firstFoundItemKey].offsetTop,
        behavior: 'smooth',
      })
    }
  }

  render() {
    const {className, values = [], selectedValue, children, onClick = noop} = this.props
    const multipleValues = Array.isArray(selectedValue)

    return (
      <ul ref={this.menuRef} className={cn('SelectMenu', {}, className)}>
        {values.map((value, index) => {
          const key =
            value.value !== undefined
              ? `${value.value}`
              : typeof value.label === 'string'
                ? value.label
                : `item_${index}`
          return (
            <li className="SelectMenu__item" key={key}>
              {React.cloneElement(
                (children || defaultChildren)(
                  value,
                  multipleValues
                    ? !isEmpty(selectedValue) &&
                        (selectedValue as Value[])
                          .map(selectedVal => selectedVal.value)
                          .includes(value.value)
                    : !!selectedValue && (selectedValue as Value).value === value.value
                ),
                {
                  ref: (ref: HTMLLIElement) => {
                    this.itemRefs[key] = ref
                  },
                  onClick: () => onClick(value),
                  multiple: multipleValues,
                }
              )}
            </li>
          )
        })}
      </ul>
    )
  }
}

export default SelectMenu
